import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, FileText, Paperclip, Phone, Send, Upload, User, UserPlus, X } from 'lucide-react';
import ContactsEditor, { ContactEntry, convertToContactEntries, convertToCreateContactDtos } from '../../components/ContactsEditor';
import {
  PageHeader,
  PageSubtitle,
  PageTitle,
  Panel,
  PortalPage,
  PrimaryButton,
  PrimaryLink,
  SecondaryButton,
} from '../../components/agent/AgentPortalUI';
import { agentPortalApi, AgentCreateLead, AgentLeadCreated } from '../../services/agentPortalService';
import { BenefitType, getBenefitTypeOptions, validateEmail, validatePhone } from '../../services/leadsService';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  DOCUMENT_ACCEPT,
  MAX_FILES_PER_BATCH,
  MAX_FILE_SIZE_MB,
  isAllowedDocumentType,
  validateFileSize,
} from '../../utils/fileUploadLimits';
import { validateIdField, validateIsraeliId } from '../../utils/validationUtils';

const REFERRALS_LEADS_PATH = '/agent/referrals?tab=leads';

interface FormState {
  benefitType: number; // 0 = לא נבחר
  otherBenefitDescription: string;
  fullName: string;
  clientIdNumber: string;
  isClientForeignResident: boolean;
  clientBirthDate: string; // YYYY-MM-DD
  clientAddress: string;
  clientCity: string;
}

type FieldName = keyof FormState | 'contacts';

const INITIAL_FORM: FormState = {
  benefitType: 0,
  otherBenefitDescription: '',
  fullName: '',
  clientIdNumber: '',
  isClientForeignResident: false,
  clientBirthDate: '',
  clientAddress: '',
  clientCity: '',
};

// סדר השדות בטופס — לגלילה לשגיאה הראשונה
const FIELD_ORDER: FieldName[] = [
  'benefitType',
  'otherBenefitDescription',
  'fullName',
  'clientIdNumber',
  'clientBirthDate',
  'contacts',
];

const todayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const formatFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;

const getNameLabel = (benefitType: number): string => {
  if (benefitType === BenefitType.DisabledChildPhysical || benefitType === BenefitType.DisabledChildMental) return 'שם הקטין';
  if (benefitType === BenefitType.NursingCare) return 'שם המבוטח';
  return 'שם הלקוח';
};

const validateField = (name: keyof FormState, form: FormState): string | null => {
  switch (name) {
    case 'benefitType':
      return form.benefitType ? null : 'יש לבחור סוג קצבה';
    case 'otherBenefitDescription':
      return form.benefitType === BenefitType.Other && !form.otherBenefitDescription.trim()
        ? 'נא לפרט את סוג הקצבה'
        : null;
    case 'fullName':
      return form.fullName.trim() ? null : 'שם מלא הוא שדה חובה';
    case 'clientIdNumber':
      if (!form.clientIdNumber.trim()) return 'תעודת זהות היא שדה חובה';
      return validateIdField(form.clientIdNumber, form.isClientForeignResident);
    case 'clientBirthDate':
      return form.clientBirthDate && form.clientBirthDate > todayIso() ? 'תאריך הלידה לא יכול להיות עתידי' : null;
    default:
      return null;
  }
};

const validateContacts = (entries: ContactEntry[]): string | null => {
  const primary = entries.find(c => c.isPrimary);
  if (!primary || !primary.number.trim()) return 'טלפון ראשי הוא שדה חובה';
  if (entries.some(c => c.number.trim() && !validatePhone(c.number.trim()))) return 'יש מספרי טלפון לא תקינים';
  if (entries.some(c => c.email.trim() && !validateEmail(c.email.trim()))) return 'יש כתובת דוא"ל לא תקינה';
  return null;
};

const scrollToField = (field: FieldName) => {
  document.querySelector(`[data-field="${field}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const AgentNewLeadPage: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [contacts, setContacts] = useState<ContactEntry[]>(() => convertToContactEntries());
  const [files, setFiles] = useState<File[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [partialResult, setPartialResult] = useState<AgentLeadCreated | null>(null);

  const setFieldError = (name: FieldName, error: string | null) => {
    setFieldErrors(prev => {
      if (!error && !prev[name]) return prev;
      const updated = { ...prev };
      if (error) updated[name] = error;
      else delete updated[name];
      return updated;
    });
  };

  const handleChange = <K extends keyof FormState>(name: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setFieldError(name, null);
    setFormError(null);
  };

  const handleBlur = (name: keyof FormState) => {
    const error = validateField(name, form);
    if (error) setFieldError(name, error);
  };

  const handleBenefitChange = (value: number) => {
    setForm(prev => ({
      ...prev,
      benefitType: value,
      otherBenefitDescription: value === BenefitType.Other ? prev.otherBenefitDescription : '',
    }));
    setFieldError('benefitType', value ? null : 'יש לבחור סוג קצבה');
    setFieldError('otherBenefitDescription', null);
  };

  const handleForeignChange = (foreign: boolean) => {
    const next = { ...form, isClientForeignResident: foreign };
    setForm(next);
    setFieldError('clientIdNumber', next.clientIdNumber.trim() ? validateField('clientIdNumber', next) : null);
  };

  const handleContactsChange = (entries: ContactEntry[]) => {
    setContacts(entries);
    setFieldError('contacts', null);
    setFormError(null);
  };

  // ----- Files -----

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;

    const accepted = [...files];
    const rejected: string[] = [];

    Array.from(incoming).forEach(file => {
      if (accepted.some(f => f.name === file.name && f.size === file.size)) return; // אותו קובץ פעמיים
      const sizeError = validateFileSize(file);
      if (!isAllowedDocumentType(file.name)) {
        rejected.push(`${file.name}: סוג הקובץ אינו נתמך`);
      } else if (file.size === 0) {
        rejected.push(`${file.name}: הקובץ ריק`);
      } else if (sizeError) {
        rejected.push(`${file.name}: ${sizeError}`);
      } else if (accepted.length >= MAX_FILES_PER_BATCH) {
        rejected.push(`${file.name}: ניתן לצרף עד ${MAX_FILES_PER_BATCH} קבצים`);
      } else {
        accepted.push(file);
      }
    });

    setFiles(accepted);
    setFileErrors(rejected);
    setFormError(null);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileErrors([]);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!submitting) addFiles(e.dataTransfer.files);
  };

  // ----- Submit -----

  const validateAll = (): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {};
    (Object.keys(INITIAL_FORM) as (keyof FormState)[]).forEach(name => {
      const error = validateField(name, form);
      if (error) errors[name] = error;
    });
    const contactsError = validateContacts(contacts);
    if (contactsError) errors.contacts = contactsError;
    return errors;
  };

  const buildDto = (): AgentCreateLead => ({
    fullName: form.fullName.trim(),
    benefitType: form.benefitType,
    otherBenefitDescription: form.benefitType === BenefitType.Other ? form.otherBenefitDescription.trim() : undefined,
    clientIdNumber: form.clientIdNumber.trim(),
    isClientForeignResident: form.isClientForeignResident,
    clientBirthDate: form.clientBirthDate || null,
    clientAddress: form.clientAddress.trim() || undefined,
    clientCity: form.clientCity.trim() || undefined,
    contacts: convertToCreateContactDtos(contacts),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const errors = validateAll();
    setFieldErrors(errors);
    const firstError = FIELD_ORDER.find(field => errors[field]);
    if (firstError) {
      scrollToField(firstError);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const result = await agentPortalApi.createLead(buildDto(), files);
      if (result.failedDocuments.length > 0) {
        setPartialResult(result);
        window.scrollTo(0, 0);
      } else {
        toast.success('הליד נשלח לצוות מגיע לך');
        navigate(REFERRALS_LEADS_PATH);
      }
    } catch (error) {
      // 409 — תעודת הזהות כבר רשומה במערכת
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setFieldError('clientIdNumber', extractErrorMessage(error));
        scrollToField('clientIdNumber');
      } else {
        setFormError(extractErrorMessage(error, 'שגיאה בשליחת הליד'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Render -----

  if (partialResult) {
    return (
      <PortalPage>
        <PageHeader>
          <PageTitle>
            <UserPlus size={26} />
            ליד חדש
          </PageTitle>
        </PageHeader>
        <Panel>
          <ResultBox>
            <ResultTitle>
              <CheckCircle2 size={22} color="var(--success-color)" />
              הליד {partialResult.fullName} נשלח לצוות מגיע לך
            </ResultTitle>
            <WarningBox role="alert">
              <AlertTriangle size={18} />
              <div>
                <strong>המסמכים הבאים לא הועלו:</strong>
                <ul>
                  {partialResult.failedDocuments.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                יש להעביר אותם לצוות מגיע לך בדרך אחרת.
              </div>
            </WarningBox>
            <div>
              <PrimaryLink to={REFERRALS_LEADS_PATH}>מעבר להפניות שלי</PrimaryLink>
            </div>
          </ResultBox>
        </Panel>
      </PortalPage>
    );
  }

  const idIsValid =
    !fieldErrors.clientIdNumber &&
    form.clientIdNumber.trim() !== '' &&
    (form.isClientForeignResident || validateIsraeliId(form.clientIdNumber.trim()));

  return (
    <PortalPage>
      <PageHeader>
        <PageTitle>
          <UserPlus size={26} />
          ליד חדש
        </PageTitle>
        <PageSubtitle>פרטי הלקוח המופנה — הצוות של מגיע לך ייצור איתו קשר</PageSubtitle>
      </PageHeader>

      <Panel as="form" onSubmit={handleSubmit} noValidate>
        <Section>
          <SectionTitle>
            <User size={18} />
            פרטי הלקוח
          </SectionTitle>

          <FieldRow>
            <Field $width="260px" data-field="benefitType">
              <Label htmlFor="benefitType">
                סוג קצבה <Required>*</Required>
              </Label>
              <Select
                id="benefitType"
                value={form.benefitType}
                onChange={e => handleBenefitChange(parseInt(e.target.value, 10))}
                onBlur={() => handleBlur('benefitType')}
                $hasError={!!fieldErrors.benefitType}
                disabled={submitting}
              >
                <option value={0}>בחר סוג קצבה</option>
                {getBenefitTypeOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {fieldErrors.benefitType && <ErrorText>{fieldErrors.benefitType}</ErrorText>}
            </Field>

            {form.benefitType === BenefitType.Other && (
              <Field $width="260px" data-field="otherBenefitDescription">
                <Label htmlFor="otherBenefitDescription">
                  פירוט סוג הקצבה <Required>*</Required>
                </Label>
                <Input
                  id="otherBenefitDescription"
                  value={form.otherBenefitDescription}
                  onChange={e => handleChange('otherBenefitDescription', e.target.value)}
                  onBlur={() => handleBlur('otherBenefitDescription')}
                  $hasError={!!fieldErrors.otherBenefitDescription}
                  placeholder="פרט את סוג הקצבה"
                  maxLength={200}
                  disabled={submitting}
                />
                {fieldErrors.otherBenefitDescription && <ErrorText>{fieldErrors.otherBenefitDescription}</ErrorText>}
              </Field>
            )}
          </FieldRow>

          <FieldRow>
            <Field $width="250px" data-field="fullName">
              <Label htmlFor="fullName">
                {getNameLabel(form.benefitType)} <Required>*</Required>
              </Label>
              <Input
                id="fullName"
                value={form.fullName}
                onChange={e => handleChange('fullName', e.target.value)}
                onBlur={() => handleBlur('fullName')}
                $hasError={!!fieldErrors.fullName}
                placeholder="שם מלא"
                maxLength={100}
                autoComplete="off"
                disabled={submitting}
              />
              {fieldErrors.fullName && <ErrorText>{fieldErrors.fullName}</ErrorText>}
            </Field>

            <Field data-field="clientIdNumber">
              <Label htmlFor="clientIdNumber">
                תעודת זהות <Required>*</Required>
              </Label>
              <InlineGroup>
                <Input
                  id="clientIdNumber"
                  value={form.clientIdNumber}
                  onChange={e => handleChange('clientIdNumber', e.target.value)}
                  onBlur={() => handleBlur('clientIdNumber')}
                  $hasError={!!fieldErrors.clientIdNumber}
                  $valid={idIsValid}
                  placeholder="123456789"
                  maxLength={form.isClientForeignResident ? 20 : 9}
                  inputMode={form.isClientForeignResident ? 'text' : 'numeric'}
                  autoComplete="off"
                  disabled={submitting}
                  style={{ width: 150, direction: 'ltr', textAlign: 'center' }}
                />
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={form.isClientForeignResident}
                    onChange={e => handleForeignChange(e.target.checked)}
                    disabled={submitting}
                  />
                  תושב חו"ל
                </CheckboxLabel>
              </InlineGroup>
              {fieldErrors.clientIdNumber && <ErrorText>{fieldErrors.clientIdNumber}</ErrorText>}
              {idIsValid && (
                <SuccessText>
                  <CheckCircle2 size={13} />
                  {form.isClientForeignResident ? 'תושב חו"ל' : 'ת.ז. תקינה'}
                </SuccessText>
              )}
            </Field>

            <Field $width="170px" data-field="clientBirthDate">
              <Label htmlFor="clientBirthDate">תאריך לידה</Label>
              <Input
                id="clientBirthDate"
                type="date"
                value={form.clientBirthDate}
                onChange={e => handleChange('clientBirthDate', e.target.value)}
                onBlur={() => handleBlur('clientBirthDate')}
                $hasError={!!fieldErrors.clientBirthDate}
                max={todayIso()}
                disabled={submitting}
              />
              {fieldErrors.clientBirthDate && <ErrorText>{fieldErrors.clientBirthDate}</ErrorText>}
            </Field>
          </FieldRow>

          <FieldRow>
            <Field $width="300px">
              <Label htmlFor="clientAddress">כתובת</Label>
              <Input
                id="clientAddress"
                value={form.clientAddress}
                onChange={e => handleChange('clientAddress', e.target.value)}
                placeholder="רחוב ומספר בית"
                maxLength={200}
                disabled={submitting}
              />
            </Field>
            <Field $width="200px">
              <Label htmlFor="clientCity">עיר</Label>
              <Input
                id="clientCity"
                value={form.clientCity}
                onChange={e => handleChange('clientCity', e.target.value)}
                placeholder="עיר מגורים"
                maxLength={100}
                disabled={submitting}
              />
            </Field>
          </FieldRow>
        </Section>

        <Section data-field="contacts">
          <SectionTitle>
            <Phone size={18} />
            אנשי קשר <Required>*</Required>
          </SectionTitle>
          <ContactsWrap>
            <ContactsEditor contacts={contacts} onChange={handleContactsChange} disabled={submitting} />
          </ContactsWrap>
          {fieldErrors.contacts && <ErrorText>{fieldErrors.contacts}</ErrorText>}
        </Section>

        <Section>
          <SectionTitle>
            <Paperclip size={18} />
            מסמכים
          </SectionTitle>
          <DropZone
            $dragOver={isDragOver}
            $disabled={submitting}
            onDragOver={e => {
              e.preventDefault();
              if (!submitting) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload size={20} />
            <span>
              גררו קבצים לכאן או <strong>בחרו מהמחשב</strong>
            </span>
            <Hint>
              PDF, Word, Excel ותמונות · עד {MAX_FILE_SIZE_MB}MB לקובץ · עד {MAX_FILES_PER_BATCH} קבצים
            </Hint>
            <input
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT}
              disabled={submitting}
              onChange={e => {
                addFiles(e.target.files);
                e.target.value = ''; // מאפשר לבחור שוב את אותו קובץ אחרי הסרה
              }}
              hidden
            />
          </DropZone>

          {fileErrors.length > 0 && (
            <ErrorList role="alert">
              {fileErrors.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ErrorList>
          )}

          {files.length > 0 && (
            <FileList>
              {files.map((file, index) => (
                <FileRow key={`${file.name}-${file.size}`}>
                  <FileText size={16} color="var(--primary-color)" />
                  <FileName title={file.name}>{file.name}</FileName>
                  <FileSize>{formatFileSize(file.size)}</FileSize>
                  <RemoveButton
                    type="button"
                    onClick={() => removeFile(index)}
                    disabled={submitting}
                    aria-label={`הסרת ${file.name}`}
                    title="הסרה"
                  >
                    <X size={16} />
                  </RemoveButton>
                </FileRow>
              ))}
            </FileList>
          )}
        </Section>

        <Footer>
          {formError && (
            <FormErrorBanner role="alert">
              <AlertTriangle size={16} />
              {formError}
            </FormErrorBanner>
          )}
          <FooterActions>
            <PrimaryButton type="submit" disabled={submitting}>
              <Send size={16} />
              {submitting ? 'שולח...' : 'שליחת הליד'}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => navigate(REFERRALS_LEADS_PATH)} disabled={submitting}>
              ביטול
            </SecondaryButton>
          </FooterActions>
        </Footer>
      </Panel>
    </PortalPage>
  );
};

export default AgentNewLeadPage;

// ----- Styles -----

const Section = styled.div`
  padding: 16px 20px 20px;
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 767px) {
    padding: 14px 16px 16px;
  }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-color);
`;

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;

  & + & {
    margin-top: 14px;
  }
`;

const Field = styled.div<{ $width?: string }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: ${p => p.$width ?? 'auto'};
  max-width: 100%;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Required = styled.span`
  color: var(--error-color);
`;

const controlStyles = (hasError?: boolean, valid?: boolean) => `
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border: 1px solid ${hasError ? 'var(--error-color)' : valid ? 'var(--success-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  background: var(--surface-color);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled {
    background: var(--background-color);
    cursor: not-allowed;
  }
`;

const Input = styled.input<{ $hasError?: boolean; $valid?: boolean }>`
  ${p => controlStyles(p.$hasError, p.$valid)}
`;

const Select = styled.select<{ $hasError?: boolean }>`
  ${p => controlStyles(p.$hasError)}
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
`;

const SuccessText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--success-color);
`;

const InlineGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
  cursor: pointer;
`;

const ContactsWrap = styled.div`
  max-width: 640px;
`;

const DropZone = styled.label<{ $dragOver: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 640px;
  padding: 18px 16px;
  border: 2px dashed ${p => (p.$dragOver ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-md);
  background: ${p => (p.$dragOver ? 'var(--primary-light)' : 'var(--background-color)')};
  color: var(--text-secondary);
  font-size: 14px;
  text-align: center;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.15s, background 0.15s;

  strong {
    color: var(--primary-color);
  }

  &:hover {
    border-color: ${p => (p.$disabled ? 'var(--border-color)' : 'var(--primary-color)')};
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: var(--text-tertiary);
`;

const ErrorList = styled.ul`
  max-width: 640px;
  margin: 10px 0 0;
  padding: 0 18px 0 0;
  font-size: 12px;
  color: var(--error-color);
`;

const FileList = styled.ul`
  list-style: none;
  max-width: 640px;
  margin: 12px 0 0;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
`;

const FileRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 12px;

  & + & {
    border-top: 1px solid var(--border-color-light);
  }
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: var(--text-tertiary);
  direction: ltr;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: var(--border-radius-sm);
  background: none;
  color: var(--text-tertiary);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--error-light);
    color: var(--error-color);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;

  @media (max-width: 767px) {
    padding: 14px 16px;
  }
`;

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const FormErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--border-radius-sm);
  background: var(--error-light);
  color: var(--error-color);
  font-size: 14px;
`;

const ResultBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const ResultTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--border-radius-sm);
  background: var(--warning-light);
  color: var(--text-primary);
  font-size: 14px;

  svg {
    flex-shrink: 0;
    color: var(--warning-color);
    margin-top: 2px;
  }

  ul {
    margin: 6px 0;
    padding: 0 18px 0 0;
  }
`;
